import { Button, Form, Row, Icon, Upload, message, Input, Spin } from "antd";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useFreire } from "../../../utils/freireContext";
import * as texts from "./locales";
import type { RcFile, UploadChangeParam } from "antd/lib/upload";
import type {
  RcCustomRequestOptions,
  UploadFile,
} from "antd/lib/upload/interface";
import api from "../../../services/api";
import axios from "axios";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";
import "./index.css";
import uploadIcon from "../../../assets/img/carregando.png";

const { Dragger } = Upload;

const MAX_IMAGES = 10;

type Image = {
  uri: string;
  description?: string;
};

type OrderedImage = { key: number } & Image;

type Props = {
  currentImages: Image[] | undefined;
  stationID: string;
  onImagesChange: (images: Image[]) => void;
  loading: boolean;
};

const GalleryImages: React.FC<Props> = ({
  currentImages,
  stationID,
  onImagesChange,
  loading,
}) => {
  const { freire } = useFreire();
  const [orderedImages, setOrderedImages] = useState<OrderedImage[]>([]);
  const [uploadFileList, setUploadFileList] = useState<UploadFile[]>([]);

  const countKeys = useRef(currentImages?.length || 0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const errorShownRef = useRef(false);

  useEffect(() => {
    const initialOrderedImages =
      currentImages?.map((image, index) => ({
        key: index,
        uri: image.uri,
        description: image?.description ?? "",
      })) ?? [];

    setOrderedImages(initialOrderedImages);
    countKeys.current = currentImages?.length ?? 0;
    errorShownRef.current = false;
  }, [currentImages]);

  useEffect(() => {
    const formatedImages =
      orderedImages
        ?.filter(({ uri }) => uri)
        ?.map(({ uri, description }) => ({ uri, description })) ?? [];

    onImagesChange(formatedImages);
  }, [onImagesChange, orderedImages]);

  const addImage = (uri: string) => {
    const newImage: OrderedImage = {
      key: countKeys.current,
      uri,
      description: "",
    };

    setOrderedImages((prev) => [...prev, newImage]);
    countKeys.current++;

    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop =
          scrollContainerRef.current.scrollHeight;
      }
    }, 250);
  };

  const onRemoveImage = (uri: string) => {
    setUploadFileList((prev) => prev.filter((file) => file.xhr !== uri));
    setOrderedImages((prev) => prev.filter((img) => img.uri !== uri));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(orderedImages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setOrderedImages(items);
  };

  const updateDescription = (uri: string, description: string) => {
    setOrderedImages((prev) =>
      prev.map((img) => (img.uri === uri ? { ...img, description } : img))
    );
  };

  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";

    if (!isJpgOrPng) {
      message.error(freire(texts.ONLY_JPG_OR_PNG_ALLOWED));
      return false;
    }

    if (orderedImages.length + fileList.length > MAX_IMAGES) {
      // Mostrar mensagem apenas uma vez
      if (!errorShownRef.current) {
        message.error(
          freire(texts.LIMIT_IMAGES_MESSAGE(MAX_IMAGES, orderedImages.length))
        );
        errorShownRef.current = true;

        setTimeout(() => {
          errorShownRef.current = false;
        }, 1000);
      }
      return false;
    }

    return true;
  };

  const onChange = (info: UploadChangeParam) => {
    const { file, fileList } = info;

    setUploadFileList(fileList.filter((file) => file.status));

    if (file.status === "done") {
      errorShownRef.current = false;
      message.success(freire(texts.UPLOAD_IMAGE_SUCCESS_MESSAGE(file.name)));
      addImage(file.xhr);
    } else if (file.status === "error") {
      errorShownRef.current = false;
      message.error(freire(texts.UPLOAD_IMAGE_ERROR_MESSAGE(file.name)));
    }
  };

  const onUpload = async ({
    onProgress,
    onError,
    onSuccess,
    file,
  }: RcCustomRequestOptions) => {
    try {
      const {
        data: { signedUrl, publicUrl },
      } = await api.get(
        `/station-gallery-images/upload/${stationID}/${file.name}`
      );

      await axios.put(signedUrl, file, {
        headers: {
          "Content-Type": file.type,
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress({ percent }, file);
        },
      });

      onSuccess(null!, publicUrl);
    } catch (error: any) {
      onError(error);
    }
  };

  const formItems = orderedImages?.map((image, index) => (
    <Draggable key={image.key} draggableId={image.key.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={`draggable-item ${snapshot.isDragging ? "dragging" : ""}`}
          style={{ ...provided.draggableProps.style }}
        >
          <Row className="draggable-item-container">
            <div {...provided.dragHandleProps} className="drag-button">
              <Icon type="drag" className="drag-button-icon" />
            </div>

            <div className="container-image-preview">
              <img
                className="image-preview"
                src={image.uri}
                alt={freire(texts.IMAGE_ALT)}
                onClick={() => {
                  window.open(image.uri, "_blank");
                }}
              />
            </div>

            <Form.Item style={{ width: "100%", margin: 0 }}>
              <span>{freire(texts.INSERT_DESCRIPTION_OPTIONAL)}</span>
              <Input
                placeholder={freire(texts.INSERT_DESCRIPTION)}
                style={{ marginTop: 5 }}
                value={image.description}
                onChange={(e) => updateDescription(image.uri, e.target.value)}
              />
            </Form.Item>

            <Button
              type="link"
              className="remove-button"
              onClick={() => onRemoveImage(image.uri)}
            >
              <span>{freire(texts.REMOVE)}</span>
            </Button>
          </Row>
        </div>
      )}
    </Draggable>
  ));

  const disabled = orderedImages.length === MAX_IMAGES || loading;

  return (
    <div id="gallery-images">
      <Form.Item
        label={freire(
          texts.GALLERY_IMAGES_LABEL(
            loading ? "--" : orderedImages?.length || 0,
            MAX_IMAGES
          )
        )}
        style={{ position: "relative" }}
      >
        <Dragger
          disabled={disabled}
          beforeUpload={beforeUpload}
          onChange={onChange}
          onRemove={(file) => onRemoveImage(file.xhr)}
          customRequest={onUpload}
          multiple={true}
          accept="image/*"
          listType="text"
          fileList={uploadFileList}
          showUploadList={uploadFileList.some(
            ({ status }) => status === "uploading" || status === "error"
          )}
          style={{ paddingTop: 0, opacity: disabled ? 0.6 : 1 }}
        >
          {loading ? (
            <Spin
              className="gallery-images-loading"
              tip={freire(texts.LOADING)}
            />
          ) : (
            <Fragment>
              <p className="ant-upload-drag-icon" style={{ margin: 0 }}>
                <img src={uploadIcon} alt="Upload" style={{ width: 100 }} />
              </p>
              <p
                className="ant-upload-text"
                style={{ fontSize: 18, margin: 0, marginTop: 6 }}
              >
                {freire(texts.DROP_IMAGES_HERE)}
              </p>
              <p
                className="ant-upload-hint"
                style={{ fontSize: 12, margin: 0 }}
              >
                {freire(texts.OR_CLICK_TO_UPLOAD)}
              </p>
            </Fragment>
          )}
        </Dragger>

        {!loading && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="images-list">
              {(provided, snapshot) => (
                <div
                  ref={(el) => {
                    provided.innerRef(el);
                    if (scrollContainerRef.current !== el) {
                      (scrollContainerRef as any).current = el;
                    }
                  }}
                  {...provided.droppableProps}
                  className={`droppable-area ${
                    snapshot.isDraggingOver ? "dragging-over" : ""
                  }`}
                >
                  {formItems}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Form.Item>
    </div>
  );
};

export default GalleryImages;
